export type ImageCategoryType = 'article' | 'articleCategory' | 'recipe' | 'recipeCategory'

export interface ImageItemRef {
  img?: string
  thumb?: string
}

export const getImageUrl = (
  pathOrItem: string | ImageItemRef | undefined,
  type: ImageCategoryType,
  preferThumb = false
): string => {
  if (!pathOrItem) return ''

  let path = ''
  if (typeof pathOrItem === 'object') {
    path = preferThumb && pathOrItem.thumb ? pathOrItem.thumb : (pathOrItem.img || '')
  } else {
    path = pathOrItem
  }

  if (!path) return ''

  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:image')) {
    return path
  }

  let folder = ''
  switch (type) {
    case 'article':
      folder = 'Articles'
      break
    case 'articleCategory':
    case 'recipeCategory':
      folder = 'ArticleCategories'
      break
    case 'recipe':
      folder = 'Recipes'
      break
  }

  if (path.startsWith('/')) {
    if (preferThumb && !path.includes('/thumb/')) {
      const pattern = new RegExp(`^/images/${folder}/`, 'i')
      if (pattern.test(path)) {
        return path.replace(pattern, `/images/${folder}/thumb/`)
      }
    }
    return path
  }

  if (preferThumb && !path.startsWith('thumb/')) {
    return `/images/${folder}/thumb/${path}`
  }

  return `/images/${folder}/${path}`
}

import { QUOTA_CONFIG } from '../constants/quotaConfig'

/**
 * Extracts dominant RGB color from an HTMLImageElement using canvas sampling.
 */
export const extractDominantColorFromImage = (img: HTMLImageElement): string => {
  try {
    const sampleCanvas = document.createElement('canvas')
    sampleCanvas.width = 1
    sampleCanvas.height = 1
    const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true })
    if (!sampleCtx) return '#8bc34a'

    sampleCtx.drawImage(img, 0, 0, 1, 1)
    const data = sampleCtx.getImageData(0, 0, 1, 1).data

    // If transparent or nearly transparent, sample a 5x5 grid for opaque pixels
    if (data[3] < 30) {
      const c5 = document.createElement('canvas')
      c5.width = 5
      c5.height = 5
      const ctx5 = c5.getContext('2d', { willReadFrequently: true })
      if (ctx5) {
        ctx5.drawImage(img, 0, 0, 5, 5)
        const d5 = ctx5.getImageData(0, 0, 5, 5).data
        for (let i = 0; i < d5.length; i += 4) {
          if (d5[i + 3] > 80) {
            return `rgb(${d5[i]}, ${d5[i + 1]}, ${d5[i + 2]})`
          }
        }
      }
      return '#8bc34a'
    }

    return `rgb(${data[0]}, ${data[1]}, ${data[2]})`
  } catch {
    return '#8bc34a'
  }
}

export interface ContentBounds {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Detects the bounding box of inner image content when an image has a uniform padding border
 * (e.g., as added by resizeAndCompressImage when fitting non-square images).
 */
export const detectContentBounds = (img: HTMLImageElement): ContentBounds => {
  const w = img.naturalWidth || img.width || 0
  const h = img.naturalHeight || img.height || 0
  const fallback: ContentBounds = { x: 0, y: 0, width: w, height: h }
  if (w <= 0 || h <= 0) return fallback

  try {
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return fallback

    ctx.drawImage(img, 0, 0, w, h)
    const imgData = ctx.getImageData(0, 0, w, h)
    const data = imgData?.data
    if (!data || data.length < w * h * 4) return fallback

    // Sample the 4 corners: TL, TR, BL, BR
    const tlIdx = 0
    const trIdx = (w - 1) * 4
    const blIdx = ((h - 1) * w) * 4
    const brIdx = (((h - 1) * w) + (w - 1)) * 4

    const colorDist = (i1: number, i2: number) => {
      return (
        Math.abs(data[i1] - data[i2]) +
        Math.abs(data[i1 + 1] - data[i2 + 1]) +
        Math.abs(data[i1 + 2] - data[i2 + 2]) +
        Math.abs(data[i1 + 3] - data[i2 + 3])
      )
    }

    // If the 4 corners are not reasonably similar in color, there is no uniform padding border
    const cornerTol = 30
    if (
      colorDist(tlIdx, trIdx) > cornerTol ||
      colorDist(tlIdx, blIdx) > cornerTol ||
      colorDist(tlIdx, brIdx) > cornerTol
    ) {
      return fallback
    }

    const bgR = data[tlIdx]
    const bgG = data[tlIdx + 1]
    const bgB = data[tlIdx + 2]
    const bgA = data[tlIdx + 3]

    const isDifferent = (idx: number) => {
      return (
        Math.abs(data[idx] - bgR) > 20 ||
        Math.abs(data[idx + 1] - bgG) > 20 ||
        Math.abs(data[idx + 2] - bgB) > 20 ||
        Math.abs(data[idx + 3] - bgA) > 20
      )
    }

    let minY = 0
    let maxY = h - 1
    let minX = 0
    let maxX = w - 1

    const minDiffRow = Math.max(2, Math.floor(w * 0.01))

    // Scan top down for minY
    for (let y = 0; y < h; y++) {
      let diff = 0
      const rowOffset = y * w * 4
      for (let x = 0; x < w; x++) {
        if (isDifferent(rowOffset + x * 4)) {
          diff++
          if (diff >= minDiffRow) break
        }
      }
      if (diff >= minDiffRow) {
        minY = y
        break
      }
    }

    // Scan bottom up for maxY
    for (let y = h - 1; y >= minY; y--) {
      let diff = 0
      const rowOffset = y * w * 4
      for (let x = 0; x < w; x++) {
        if (isDifferent(rowOffset + x * 4)) {
          diff++
          if (diff >= minDiffRow) break
        }
      }
      if (diff >= minDiffRow) {
        maxY = y
        break
      }
    }

    const colHeight = maxY - minY + 1
    const minDiffCol = Math.max(2, Math.floor(colHeight * 0.01))

    // Scan left to right for minX
    for (let x = 0; x < w; x++) {
      let diff = 0
      for (let y = minY; y <= maxY; y++) {
        if (isDifferent((y * w + x) * 4)) {
          diff++
          if (diff >= minDiffCol) break
        }
      }
      if (diff >= minDiffCol) {
        minX = x
        break
      }
    }

    // Scan right to left for maxX
    for (let x = w - 1; x >= minX; x--) {
      let diff = 0
      for (let y = minY; y <= maxY; y++) {
        if (isDifferent((y * w + x) * 4)) {
          diff++
          if (diff >= minDiffCol) break
        }
      }
      if (diff >= minDiffCol) {
        maxX = x
        break
      }
    }

    const detectedW = maxX - minX + 1
    const detectedH = maxY - minY + 1

    // If detected bounds are almost the entire image (e.g. less than 4px padding), return fallback
    if (minX <= 4 && minY <= 4 && w - maxX <= 5 && h - maxY <= 5) {
      return fallback
    }

    if (detectedW <= 10 || detectedH <= 10) {
      return fallback
    }

    return {
      x: minX,
      y: minY,
      width: detectedW,
      height: detectedH
    }
  } catch {
    return fallback
  }
}

/**
 * Automatically crops an image to its inner content area (or square-cropped inner content),
 * stripping any solid/dominant colored padding border that was added to fit non-square formats.
 */
export const cropImageToContent = (
  dataUrl: string,
  targetSize = 500,
  quality = 0.82
): Promise<string> => {
  return new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) {
      resolve(dataUrl)
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const bounds = detectContentBounds(img)
      const natW = img.naturalWidth || img.width
      const natH = img.naturalHeight || img.height

      // If no padding was detected (bounds match full image), return original dataUrl
      if (bounds.x === 0 && bounds.y === 0 && bounds.width === natW && bounds.height === natH) {
        resolve(dataUrl)
        return
      }

      // Crop to a square encompassing the detected content, matching ModalImageCropper's behavior
      const size = Math.min(bounds.width, bounds.height)
      const cropX = Math.max(0, Math.min(natW - size, bounds.x + (bounds.width - size) / 2))
      const cropY = Math.max(0, Math.min(natH - size, bounds.y + (bounds.height - size) / 2))

      const canvas = document.createElement('canvas')
      canvas.width = targetSize
      canvas.height = targetSize
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(dataUrl)
        return
      }

      ctx.drawImage(img, cropX, cropY, size, size, 0, 0, targetSize, targetSize)

      try {
        const webpUrl = canvas.toDataURL('image/webp', quality)
        if (webpUrl.startsWith('data:image/webp')) {
          resolve(webpUrl)
          return
        }
        resolve(canvas.toDataURL('image/jpeg', quality))
      } catch {
        resolve(dataUrl)
      }
    }
    img.onerror = () => resolve(dataUrl)
    img.src = dataUrl
  })
}

/**
 * Resizes and compresses an image Data URL to WebP format using Canvas.
 * Automatically downscales/compresses if output exceeds QUOTA_CONFIG.MAX_IMAGE_BASE64_BYTES.
 */
export const resizeAndCompressImage = (
  dataUrl: string,
  targetSize: number,
  quality = 0.8
): Promise<string> => {
  return new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) {
      resolve(dataUrl)
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      let curSize = targetSize
      let curQuality = quality

      const render = (s: number, q: number): string => {
        const canvas = document.createElement('canvas')
        const rawW = img.naturalWidth || img.width
        const rawH = img.naturalHeight || img.height

        if (rawW <= 0 || rawH <= 0) return dataUrl

        const aspectRatio = rawW / rawH
        const maxRatio = Math.max(aspectRatio, 1 / aspectRatio)

        // If aspect ratio deviates from square (e.g. > 1.20, like 4:3, 16:9, etc.),
        // pad with a white background on a square canvas so the full image is visible in circular icons!
        if (maxRatio > 1.20) {
          canvas.width = s
          canvas.height = s

          const ctx = canvas.getContext('2d')
          if (!ctx) return dataUrl

          // Fill canvas with calculated dominant color from the image (not white)
          ctx.fillStyle = extractDominantColorFromImage(img)
          ctx.fillRect(0, 0, s, s)

          // Calculate fitted drawing dimensions so wide/tall image fits inside circular icon
          const maxDiagonal = s * 0.94
          const naturalDiagonal = Math.sqrt(rawW * rawW + rawH * rawH)
          const scale = naturalDiagonal > 0 ? maxDiagonal / naturalDiagonal : 1

          // Ensure it doesn't exceed 94% of canvas width or height
          const boundScale = Math.min(scale, (s * 0.94) / rawW, (s * 0.94) / rawH)

          const drawW = Math.max(1, Math.round(rawW * boundScale))
          const drawH = Math.max(1, Math.round(rawH * boundScale))
          const offsetX = Math.round((s - drawW) / 2)
          const offsetY = Math.round((s - drawH) / 2)

          ctx.drawImage(img, offsetX, offsetY, drawW, drawH)
        } else {
          // Nearly square image: scale proportionally without padding
          let width = rawW
          let height = rawH

          if (width > s || height > s) {
            if (width > height) {
              height = Math.round((height * s) / width)
              width = s
            } else {
              width = Math.round((width * s) / height)
              height = s
            }
          }

          canvas.width = Math.max(1, width)
          canvas.height = Math.max(1, height)

          const ctx = canvas.getContext('2d')
          if (!ctx) return dataUrl

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        }
        try {
          const webpUrl = canvas.toDataURL('image/webp', q)
          if (webpUrl.startsWith('data:image/webp')) {
            return webpUrl
          }
          return canvas.toDataURL('image/jpeg', q)
        } catch {
          return dataUrl
        }
      }

      let result = render(curSize, curQuality)
      const maxAllowedBytes = QUOTA_CONFIG.MAX_IMAGE_BASE64_BYTES

      // Multi-pass step down if image exceeds maximum allowed Base64 size
      let attempts = 0
      while (result.length > maxAllowedBytes && attempts < 4) {
        attempts++
        curQuality = Math.max(0.4, curQuality - 0.15)
        curSize = Math.max(200, Math.round(curSize * 0.8))
        result = render(curSize, curQuality)
      }

      resolve(result)
    }
    img.onerror = () => resolve(dataUrl)
    img.src = dataUrl
  })
}

/**
 * Downloads an external image from a URL, bypasses CORS restrictions via
 * direct fetch, canvas loading, or proxy fallback, and returns a compressed WebP Data URL.
 * Ensures images remain permanently available even if the remote host removes them.
 */
export const downloadAndConvertImageUrl = async (url: string): Promise<string | null> => {
  if (!url || typeof url !== 'string' || !url.trim()) return null
  const cleanUrl = url.trim()
  if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) return null

  const blobToDataUrl = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  const canvasToDataUrl = (img: HTMLImageElement): string => {
    const canvas = document.createElement('canvas')
    const width = img.naturalWidth || img.width || 800
    const height = img.naturalHeight || img.height || 800
    canvas.width = Math.max(1, width)
    canvas.height = Math.max(1, height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas context not available')
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    try {
      return canvas.toDataURL('image/webp', 0.85)
    } catch {
      return canvas.toDataURL('image/jpeg', 0.85)
    }
  }

  const fetchWithTimeout = (targetUrl: string, timeoutMs = 6000): Promise<Response> => {
    return Promise.race([
      fetch(targetUrl),
      new Promise<Response>((_, reject) =>
        setTimeout(() => reject(new Error(`Fetch timeout for ${targetUrl}`)), timeoutMs)
      )
    ])
  }

  const tryLoadImage = (src: string, timeoutMs = 6000): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      let timer: ReturnType<typeof setTimeout> | null = null

      const cleanup = () => {
        if (timer) clearTimeout(timer)
        img.onload = null
        img.onerror = null
      }

      timer = setTimeout(() => {
        cleanup()
        reject(new Error(`Timeout loading image: ${src}`))
      }, timeoutMs)

      img.onload = () => {
        cleanup()
        try {
          resolve(canvasToDataUrl(img))
        } catch (e) {
          reject(e)
        }
      }
      img.onerror = (e) => {
        cleanup()
        reject(e)
      }
      img.src = src
    })
  }

  // 1. Direct fetch (fastest for servers with open CORS headers)
  try {
    const res = await fetchWithTimeout(cleanUrl, 6000)
    if (res.ok) {
      const blob = await res.blob()
      if (blob && (blob.type.startsWith('image/') || blob.type === 'application/octet-stream')) {
        const rawDataUrl = await blobToDataUrl(blob)
        return await resizeAndCompressImage(rawDataUrl, 800, 0.82)
      }
    }
  } catch {
    // Continue to next strategy if direct fetch is blocked by CORS
  }

  // 2. HTMLImageElement with anonymous crossOrigin
  try {
    const rawDataUrl = await tryLoadImage(cleanUrl, 6000)
    if (rawDataUrl) {
      return await resizeAndCompressImage(rawDataUrl, 800, 0.82)
    }
  } catch {
    // Continue to proxy strategy
  }

  // 3. Weserv Image Proxy (free, fast, sets Access-Control-Allow-Origin: *)
  try {
    const cleanNoProto = cleanUrl.replace(/^https?:\/\//, '')
    const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(cleanNoProto)}&output=webp&q=82`
    const rawDataUrl = await tryLoadImage(proxyUrl, 6000)
    if (rawDataUrl) {
      return await resizeAndCompressImage(rawDataUrl, 800, 0.82)
    }
  } catch {
    // Continue to next fallback
  }

  // 4. AllOrigins fallback
  try {
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(cleanUrl)}`
    const res = await fetchWithTimeout(proxyUrl, 6000)
    if (res.ok) {
      const blob = await res.blob()
      if (blob) {
        const rawDataUrl = await blobToDataUrl(blob)
        return await resizeAndCompressImage(rawDataUrl, 800, 0.82)
      }
    }
  } catch {
    // All download strategies failed
  }

  return null
}
